import React, { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { X, MapPin, Sparkles, Package, ChevronLeft, ChevronRight } from 'lucide-react';
import { Link } from 'react-router-dom';
import ProductPopup from './ProductPopup';

// Bottle popup perfumes
const BOTTLE_PERFUMES = [
  { id: 'oud-royal', name: 'OUD ROYAL', subtitle: 'Majestic Heritage', price: '$495.00', origin: 'Laos & Arabia', description: 'The crown jewel featuring the finest Laotian oud aged for decades.', notes: 'Oud • Frankincense • Turkish Rose', image: 'https://images.unsplash.com/photo-1757313231524-6504c839ca0b?crop=entropy&cs=srgb&fm=jpg&w=400&q=80' },
  { id: 'golden-aura', name: 'GOLDEN AURA', subtitle: 'Radiant Sophistication', price: '$345.00', origin: 'Persia & India', description: 'Precious saffron with honey-dipped florals and rich amber.', notes: 'Saffron • Tuberose • Amber', image: 'https://images.unsplash.com/photo-1757313202626-8b763ce254a1?crop=entropy&cs=srgb&fm=jpg&w=400&q=80' },
  { id: 'sultans-treasure', name: "SULTAN'S TREASURE", subtitle: 'Royal Indulgence', price: '$550.00', origin: 'Cambodia & Arabia', description: 'Rare Cambodian oud with 24-karat gold-infused amber.', notes: 'Cambodian Oud • Rose Otto • 24K Amber', image: 'https://images.unsplash.com/photo-1770301410072-f6ef6dad65b2?crop=entropy&cs=srgb&fm=jpg&w=400&q=80' }
];

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const EyeCatchingDivider = ({ variant = 'geometric' }) => {
  const [selectedBottle, setSelectedBottle] = useState(null);
  const [flippedCard, setFlippedCard] = useState(null);
  const [allProducts, setAllProducts] = useState([]);

  useEffect(() => {
    if (variant === 'floating-bottles') {
      fetch(`${BACKEND_URL}/api/products`)
        .then(r => r.json())
        .then(data => setAllProducts(data))
        .catch(() => {});
    }
  }, [variant]);

  if (variant === 'geometric') {
    return (
      <section className="relative py-16 bg-[#050505] overflow-hidden" data-testid="divider-geometric">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="flex items-center justify-center">
            <div className="flex-1 h-px bg-gradient-to-r from-transparent via-[#D4AF37]/50 to-[#D4AF37]" />
            <div className="mx-8 geometric-spin">
              <div className="relative w-20 h-20">
                <div className="absolute inset-0 border border-[#D4AF37]/50 rotate-45" />
                <div className="absolute inset-2 border border-[#D4AF37]/30 rotate-45" />
                <div className="absolute inset-4 border border-[#D4AF37]/20 rotate-45" />
                <div className="absolute inset-0 flex items-center justify-center">
                  <span className="font-arabic text-2xl text-[#D4AF37]">✧</span>
                </div>
              </div>
            </div>
            <div className="flex-1 h-px bg-gradient-to-l from-transparent via-[#D4AF37]/50 to-[#D4AF37]" />
          </div>
        </div>
      </section>
    );
  }

  if (variant === 'floating-bottles') {
    // Generate sandstorm particles
    const sandParticles = Array.from({ length: 50 }, (_, i) => ({
      id: i,
      size: Math.random() * 4 + 1,
      x: Math.random() * 100,
      y: Math.random() * 100,
      duration: Math.random() * 3 + 2,
      delay: Math.random() * 2,
      opacity: Math.random() * 0.6 + 0.2,
    }));

    const handleCardClick = () => {
      // Pick a random perfume from ALL products
      if (allProducts.length > 0) {
        const random = allProducts[Math.floor(Math.random() * allProducts.length)];
        setSelectedBottle(random);
      }
    };

    const handlePopupClose = () => {
      setSelectedBottle(null);
    };

    return (
      <section className="relative py-28 md:py-36" data-testid="divider-bottles" style={{
        background: 'linear-gradient(180deg, #050505 0%, #0a0a0a 30%, #080808 50%, #0a0a0a 70%, #050505 100%)',
      }}>
        <div className="max-w-7xl mx-auto px-6 md:px-12 relative">
          
          {/* Title */}
          <p className="text-center font-heading text-xs tracking-[0.3em] text-white/25 mb-14">TAP TO DISCOVER</p>

          {/* 3 Arabian mystery cards */}
          <div className="flex items-center justify-center gap-14 md:gap-20 relative">
            {[
              { glow: 'rgba(140,130,120,0.15)', delay: 0 },
              { glow: 'rgba(160,130,115,0.15)', delay: 0.8 },
              { glow: 'rgba(140,130,120,0.15)', delay: 1.6 },
            ].map((theme, i) => (
              <button
                key={i}
                onClick={handleCardClick}
                className="relative focus:outline-none group"
                data-testid={`bottle-${i}`}
              >
                <div className="bottle-float relative w-28 h-40 md:w-32 md:h-44 flex items-center justify-center transition-all duration-300 group-hover:shadow-[0_0_18px_var(--glow)]" style={{ animationDelay: `${theme.delay}s`, border: '1px solid #2a2520', background: '#0a0908', '--glow': theme.glow }}>
                  {/* Inner border */}
                  <div className="absolute inset-1.5 pointer-events-none" style={{ border: '1px solid #1e1a16' }} />

                  {/* Arabian geometric pattern */}
                  <svg width="60" height="80" viewBox="0 0 60 80" className="opacity-[0.12]">
                    {/* Central diamond */}
                    <polygon points="30,8 52,40 30,72 8,40" fill="none" stroke="#8a7a60" strokeWidth="1" />
                    <polygon points="30,18 44,40 30,62 16,40" fill="none" stroke="#8a7a60" strokeWidth="0.6" />
                    {/* Center star */}
                    <circle cx="30" cy="40" r="6" fill="none" stroke="#8a7a60" strokeWidth="0.8" />
                    <circle cx="30" cy="40" r="2.5" fill="#8a7a60" opacity="0.5" />
                    {/* Top arch */}
                    <path d="M20,12 Q30,4 40,12" fill="none" stroke="#8a7a60" strokeWidth="0.7" />
                    {/* Bottom arch */}
                    <path d="M20,68 Q30,76 40,68" fill="none" stroke="#8a7a60" strokeWidth="0.7" />
                    {/* Side dots */}
                    <circle cx="8" cy="40" r="1.5" fill="#8a7a60" opacity="0.4" />
                    <circle cx="52" cy="40" r="1.5" fill="#8a7a60" opacity="0.4" />
                    <circle cx="30" cy="8" r="1.5" fill="#8a7a60" opacity="0.4" />
                    <circle cx="30" cy="72" r="1.5" fill="#8a7a60" opacity="0.4" />
                  </svg>

                  {/* Corner arabesques */}
                  {['top-1 left-1', 'top-1 right-1 scale-x-[-1]', 'bottom-1 left-1 scale-y-[-1]', 'bottom-1 right-1 scale-[-1]'].map((pos, ci) => (
                    <div key={ci} className={`absolute ${pos} pointer-events-none`}>
                      <svg width="14" height="14" viewBox="0 0 14 14" className="opacity-[0.18]">
                        <path d="M0,0 C5,0 7,2 7,7 L6,7 C6,3 4,1 0,1 Z" fill="#8a7a60" />
                      </svg>
                    </div>
                  ))}
                </div>
                <p className="mt-4 font-arabic text-sm text-white/10 group-hover:text-white/20 transition-colors text-center">؟</p>
              </button>
            ))}
          </div>

          <p className="text-center mt-12 font-arabic text-xl text-white/10">اكتشف عطرك</p>
        </div>

        {/* Gender-aware popup via ProductPopup */}
        {selectedBottle && createPortal(
          <ProductPopup product={selectedBottle} gender={selectedBottle.gender} onClose={handlePopupClose} />,
          document.body
        )}
      </section>
    );
  }

  if (variant === 'arabian-arch') return <TreasureChest />;

  return null;
};

// Ancient Mythical Treasure Chest — A legendary artifact discovery
const TreasureChest = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [products, setProducts] = useState([]);
  const [popup, setPopup] = useState(null);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const treasuryScrollRef = useRef(null);

  const scrollTreasury = (direction) => {
    if (treasuryScrollRef.current) {
      treasuryScrollRef.current.scrollBy({
        left: direction === 'left' ? -300 : 300,
        behavior: 'smooth'
      });
    }
  };

  useEffect(() => {
    const fetchProducts = async () => {
      try {
        const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/products`);
        const data = await res.json();
        const pool = data.filter(p => p.image);
        for (let i = pool.length - 1; i > 0; i--) {
          const j = Math.floor(Math.random() * (i + 1));
          [pool[i], pool[j]] = [pool[j], pool[i]];
        }
        setProducts(pool.slice(0, 20));
      } catch (e) { console.error(e); }
    };
    fetchProducts();
  }, []);

  const scrollToMap = (productId) => {
    setPopup(null);
    const mapEl = document.querySelector('[data-testid="story-preview-section"]');
    if (mapEl) {
      mapEl.scrollIntoView({ behavior: 'smooth' });
      window.dispatchEvent(new CustomEvent('highlightPerfume', { detail: { productId } }));
    }
  };

  const handleChestClick = () => {
    if (!isOpen) setIsOpen(true);
  };

  return (
    <section className="relative bg-[#020202] overflow-hidden py-20 md:py-32" data-testid="divider-arch">
      {/* Ancient temple atmosphere */}
      <div className="absolute inset-0 pointer-events-none">
        {/* Dark mysterious gradient */}
        <div className="absolute inset-0" style={{
          background: 'radial-gradient(ellipse at 50% 30%, #0d0a06 0%, #020202 50%, #000 100%)'
        }} />
        
        {/* Floating dust particles */}
        <div className="absolute inset-0 dust-particles" />
        
        {/* Mystical fog at bottom */}
        <div className="absolute bottom-0 left-0 right-0 h-40" style={{
          background: 'linear-gradient(to top, rgba(212,175,55,0.03) 0%, transparent 100%)'
        }} />
        
        {/* Ancient stone texture overlay */}
        <div className="absolute inset-0 opacity-[0.03]" style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 100 100' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noise'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.8' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noise)'/%3E%3C/svg%3E")`
        }} />
      </div>

      <div className="relative max-w-6xl mx-auto px-4">
        {/* Ancient Header */}
        <div className="text-center mb-10 md:mb-16">
          <div className="inline-block mb-4">
            <div className="flex items-center justify-center gap-4">
              <div className="w-16 h-px bg-gradient-to-r from-transparent via-[#D4AF37]/50 to-[#D4AF37]" />
              <span className="text-[#D4AF37]/40 text-2xl">✦</span>
              <div className="w-16 h-px bg-gradient-to-l from-transparent via-[#D4AF37]/50 to-[#D4AF37]" />
            </div>
          </div>
          <p className="font-arabic text-4xl md:text-5xl text-[#D4AF37]/40 mb-4 ancient-glow">الكنز المقدس</p>
          <p className="font-heading text-[10px] tracking-[0.5em] text-[#8B6914] mb-4">DISCOVERED IN THE SANDS OF TIME</p>
          <h2 className="font-heading text-4xl md:text-6xl tracking-wider text-white mb-4">
            THE <span className="text-[#D4AF37] ancient-glow">SACRED</span> TREASURY
          </h2>
          <p className="font-body text-white/30 text-sm max-w-lg mx-auto italic">
            {isOpen 
              ? '"Within lies the essence of a thousand years..."' 
              : '"An ancient chest, sealed for centuries, awaits the worthy..."'}
          </p>
        </div>

        {/* The Ancient Chest */}
        <div 
          className={`relative mx-auto cursor-pointer transition-all duration-700 ${isOpen ? 'mb-12' : 'mb-8'}`}
          style={{ maxWidth: '580px', perspective: '2000px' }}
          onClick={handleChestClick}
          data-testid="treasure-chest-clickable"
        >
          {/* Ethereal glow aura */}
          <div className="absolute -inset-20 pointer-events-none" style={{
            background: isOpen 
              ? 'radial-gradient(ellipse at 50% 60%, rgba(212,175,55,0.35) 0%, rgba(212,175,55,0.1) 30%, transparent 60%)'
              : 'radial-gradient(ellipse at 50% 80%, rgba(212,175,55,0.08) 0%, transparent 40%)',
            transition: 'all 2s ease-out',
            filter: 'blur(20px)'
          }} />
          
          {/* Mystical light rays when open */}
          {isOpen && (
            <div className="absolute -inset-10 pointer-events-none overflow-hidden">
              {[...Array(8)].map((_, i) => (
                <div key={i} className="absolute top-1/2 left-1/2 w-1 origin-bottom light-ray" style={{
                  height: '200px',
                  background: 'linear-gradient(to top, rgba(212,175,55,0.3) 0%, transparent 100%)',
                  transform: `translate(-50%, -100%) rotate(${i * 45}deg)`,
                  animationDelay: `${i * 0.1}s`
                }} />
              ))}
            </div>
          )}

          {/* Ground shadow */}
          <div className="absolute -bottom-4 left-1/2 -translate-x-1/2 w-[90%] h-8 rounded-[100%]" style={{
            background: 'radial-gradient(ellipse, rgba(0,0,0,0.6) 0%, transparent 70%)',
            filter: 'blur(8px)'
          }} />

          {/* THE CHEST */}
          <div className="relative" style={{ transformStyle: 'preserve-3d' }}>
            
            {/* === LID === */}
            <div
              className="relative z-10"
              style={{
                transform: isOpen ? 'rotateX(-115deg) translateY(-5px)' : 'rotateX(0deg)',
                transformOrigin: 'top center',
                transition: 'transform 2s cubic-bezier(0.16, 1, 0.3, 1)',
                transformStyle: 'preserve-3d',
              }}
            >
              {/* Lid Top */}
              <div className="h-32 md:h-40 relative overflow-hidden" style={{
                background: `
                  linear-gradient(180deg, 
                    #2a1f10 0%, 
                    #1a1408 15%,
                    #0f0a04 40%,
                    #1a1408 60%,
                    #2a1f10 100%
                  )`,
                borderRadius: '30px 30px 0 0',
                border: '5px solid',
                borderColor: '#3d2d14',
                borderBottom: 'none',
                boxShadow: `
                  inset 0 -30px 60px rgba(0,0,0,0.8),
                  inset 0 10px 30px rgba(212,175,55,0.05),
                  0 -15px 40px rgba(0,0,0,0.5)
                `,
              }}>
                {/* Weathered wood texture */}
                <div className="absolute inset-0" style={{
                  background: `
                    repeating-linear-gradient(0deg, transparent, transparent 3px, rgba(0,0,0,0.15) 3px, rgba(0,0,0,0.15) 4px),
                    repeating-linear-gradient(90deg, transparent, transparent 40px, rgba(0,0,0,0.1) 40px, rgba(0,0,0,0.1) 41px)
                  `
                }} />
                
                {/* Ancient cracks and wear */}
                <div className="absolute inset-0 opacity-30" style={{
                  background: `
                    linear-gradient(45deg, transparent 30%, rgba(0,0,0,0.3) 31%, transparent 32%),
                    linear-gradient(-60deg, transparent 60%, rgba(0,0,0,0.2) 61%, transparent 62%)
                  `
                }} />

                {/* Ornate center band with ancient runes */}
                <div className="absolute inset-x-0 top-1/2 -translate-y-1/2 h-14 md:h-16 mx-8">
                  {/* Weathered gold band */}
                  <div className="absolute inset-0" style={{
                    background: `linear-gradient(90deg, 
                      #2a1f10 0%,
                      #4a3510 5%,
                      #6B5210 15%, 
                      #8B6914 30%, 
                      #A67C00 50%, 
                      #8B6914 70%, 
                      #6B5210 85%,
                      #4a3510 95%,
                      #2a1f10 100%
                    )`,
                    boxShadow: 'inset 0 2px 4px rgba(255,255,255,0.1), inset 0 -2px 4px rgba(0,0,0,0.3), 0 4px 15px rgba(0,0,0,0.5)',
                    borderTop: '2px solid #5a4a20',
                    borderBottom: '2px solid #3a2a10',
                  }}>
                    {/* Ancient rune symbols */}
                    <div className="absolute inset-0 flex items-center justify-center gap-6">
                      {['☽', '✧', '◇', '✦', '☀', '✦', '◇', '✧', '☾'].map((rune, i) => (
                        <span key={i} className="text-[#0a0804] text-lg md:text-xl opacity-60" style={{
                          textShadow: '0 1px 0 rgba(255,255,255,0.1)'
                        }}>{rune}</span>
                      ))}
                    </div>
                  </div>
                  
                  {/* Center medallion */}
                  <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-16 h-16 md:w-20 md:h-20">
                    <div className="absolute inset-0 rounded-full" style={{
                      background: 'radial-gradient(circle at 35% 35%, #C9A227 0%, #8B6914 40%, #4a3510 80%, #2a1f10 100%)',
                      boxShadow: 'inset 0 3px 6px rgba(255,255,255,0.2), inset 0 -3px 6px rgba(0,0,0,0.4), 0 4px 20px rgba(0,0,0,0.6)',
                      border: '3px solid #3d2d14'
                    }}>
                      {/* Inner emblem */}
                      <div className="absolute inset-2 rounded-full flex items-center justify-center" style={{
                        background: 'radial-gradient(circle, #0a0804 0%, #1a1408 100%)',
                        border: '2px solid #6B5210'
                      }}>
                        <span className="text-[#A67C00] text-2xl md:text-3xl ancient-glow">❂</span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Corner reinforcements - weathered brass */}
                {[
                  'top-3 left-3', 'top-3 right-3', 
                  'bottom-3 left-3', 'bottom-3 right-3'
                ].map((pos, i) => (
                  <div key={i} className={`absolute ${pos}`}>
                    <div className="w-10 h-10 md:w-12 md:h-12 relative">
                      {/* L-shaped bracket */}
                      <div className="absolute inset-0" style={{
                        background: `linear-gradient(${i < 2 ? '135deg' : '-45deg'}, #6B5210 0%, #4a3510 50%, #2a1f10 100%)`,
                        clipPath: i === 0 ? 'polygon(0 0, 100% 0, 100% 30%, 30% 30%, 30% 100%, 0 100%)' :
                                  i === 1 ? 'polygon(0 0, 100% 0, 100% 100%, 70% 100%, 70% 30%, 0 30%)' :
                                  i === 2 ? 'polygon(0 0, 30% 0, 30% 70%, 100% 70%, 100% 100%, 0 100%)' :
                                           'polygon(70% 0, 100% 0, 100% 100%, 0 100%, 0 70%, 70% 70%)',
                        boxShadow: 'inset 0 1px 2px rgba(255,255,255,0.1)'
                      }} />
                      {/* Rivet */}
                      <div className="absolute w-3 h-3 rounded-full" style={{
                        top: i < 2 ? '8px' : 'auto',
                        bottom: i >= 2 ? '8px' : 'auto',
                        left: i % 2 === 0 ? '8px' : 'auto',
                        right: i % 2 === 1 ? '8px' : 'auto',
                        background: 'radial-gradient(circle at 30% 30%, #8B6914, #4a3510)',
                        boxShadow: 'inset 0 1px 1px rgba(255,255,255,0.2), 0 1px 2px rgba(0,0,0,0.4)'
                      }} />
                    </div>
                  </div>
                ))}

                {/* Side hinges */}
                {['left-0 top-[40%]', 'right-0 top-[40%]'].map((pos, i) => (
                  <div key={i} className={`absolute ${pos} w-4 h-12`} style={{
                    background: 'linear-gradient(90deg, #4a3510, #6B5210, #4a3510)',
                    borderRadius: i === 0 ? '4px 0 0 4px' : '0 4px 4px 0',
                    boxShadow: 'inset 0 2px 4px rgba(255,255,255,0.1)'
                  }} />
                ))}
              </div>

              {/* Lid inner face */}
              <div className="absolute inset-x-0 top-full h-32 md:h-40" style={{
                background: 'linear-gradient(180deg, #0a0804 0%, #15100a 50%, #1a1408 100%)',
                transform: 'rotateX(90deg)',
                transformOrigin: 'top center',
                border: '5px solid #3d2d14',
                borderTop: 'none',
              }}>
                {/* Inner carving */}
                <div className="absolute inset-4 border border-[#6B5210]/30 flex items-center justify-center">
                  <div className="text-center">
                    <p className="font-arabic text-[#A67C00]/40 text-xl">كنوز الشرق</p>
                    <p className="font-heading text-[8px] tracking-widest text-[#6B5210]/40 mt-1">TREASURES OF THE EAST</p>
                  </div>
                </div>
              </div>
            </div>

            {/* === CHEST BODY === */}
            <div className="relative" style={{
              background: `linear-gradient(180deg, 
                #2a1f10 0%, 
                #1a1408 20%,
                #0f0a04 50%,
                #0a0804 80%,
                #050402 100%
              )`,
              border: '5px solid #3d2d14',
              borderTop: '6px solid #4a3510',
              height: isOpen ? '80px' : '130px',
              transition: 'height 1s ease-out',
              boxShadow: `
                0 30px 80px rgba(0,0,0,0.8),
                inset 0 15px 50px rgba(0,0,0,0.6),
                inset 0 -10px 30px rgba(212,175,55,0.03)
              `,
            }}>
              {/* Weathered wood grain */}
              <div className="absolute inset-0" style={{
                background: `
                  repeating-linear-gradient(0deg, transparent, transparent 4px, rgba(0,0,0,0.2) 4px, rgba(0,0,0,0.2) 5px),
                  repeating-linear-gradient(90deg, transparent, transparent 50px, rgba(0,0,0,0.1) 50px, rgba(0,0,0,0.1) 51px)
                `
              }} />

              {/* Ancient lock mechanism */}
              {!isOpen && (
                <div className="absolute top-0 left-1/2 -translate-x-1/2 -translate-y-1/2 z-20">
                  <div className="relative">
                    {/* Lock plate */}
                    <div className="w-16 h-20 relative" style={{
                      background: 'linear-gradient(180deg, #6B5210 0%, #4a3510 30%, #2a1f10 100%)',
                      borderRadius: '6px 6px 12px 12px',
                      boxShadow: '0 6px 20px rgba(0,0,0,0.6), inset 0 2px 4px rgba(255,255,255,0.1)',
                      border: '2px solid #3d2d14'
                    }}>
                      {/* Lock face */}
                      <div className="absolute inset-2 rounded" style={{
                        background: 'radial-gradient(circle at 50% 30%, #1a1408 0%, #0a0804 100%)',
                        border: '1px solid #4a3510'
                      }}>
                        {/* Keyhole */}
                        <div className="absolute top-3 left-1/2 -translate-x-1/2">
                          <div className="w-4 h-4 rounded-full bg-[#000] border border-[#4a3510]" />
                          <div className="w-2 h-5 bg-[#000] mx-auto -mt-1 border-x border-b border-[#4a3510]" />
                        </div>
                        {/* Ancient symbol */}
                        <div className="absolute bottom-2 left-1/2 -translate-x-1/2">
                          <span className="text-[#6B5210] text-xs">⚜</span>
                        </div>
                      </div>
                    </div>
                    {/* Mystical glow around lock */}
                    <div className="absolute -inset-2 rounded-xl pointer-events-none lock-pulse" style={{
                      background: 'radial-gradient(circle, rgba(212,175,55,0.2) 0%, transparent 70%)',
                    }} />
                  </div>
                </div>
              )}

              {/* Magical glow from inside when open */}
              <div className="absolute inset-0 pointer-events-none overflow-hidden" style={{
                background: isOpen 
                  ? `radial-gradient(ellipse at center -20%, 
                      rgba(255,215,0,0.5) 0%, 
                      rgba(212,175,55,0.3) 20%,
                      rgba(212,175,55,0.1) 40%, 
                      transparent 60%
                    )`
                  : 'none',
                transition: 'all 1.5s ease-out 0.5s'
              }} />

              {/* Bottom reinforcement band */}
              <div className="absolute inset-x-0 bottom-0 h-6" style={{
                background: 'linear-gradient(90deg, #2a1f10, #4a3510 20%, #6B5210 50%, #4a3510 80%, #2a1f10)',
                borderTop: '2px solid #3d2d14',
              }}>
                {/* Aged rivets */}
                {[15, 30, 50, 70, 85].map(pos => (
                  <div key={pos} className="absolute top-1/2 -translate-y-1/2 w-2 h-2 rounded-full" style={{
                    left: `${pos}%`,
                    background: 'radial-gradient(circle at 30% 30%, #6B5210, #2a1f10)',
                    boxShadow: 'inset 0 1px 1px rgba(255,255,255,0.1)'
                  }} />
                ))}
              </div>

              {/* Side reinforcement straps */}
              {[12, 88].map(pos => (
                <div key={pos} className="absolute top-0 bottom-0 w-8" style={{
                  left: `${pos}%`,
                  transform: 'translateX(-50%)',
                  background: 'linear-gradient(90deg, transparent, #4a3510 30%, #6B5210 50%, #4a3510 70%, transparent)',
                  opacity: 0.6
                }} />
              ))}
            </div>
          </div>

          {/* Mystical hint when closed */}
          {!isOpen && (
            <div className="absolute -bottom-20 left-1/2 -translate-x-1/2 text-center">
              <div className="flex items-center justify-center gap-3 mb-2">
                <div className="w-8 h-px bg-gradient-to-r from-transparent to-[#D4AF37]/50" />
                <span className="text-[#D4AF37]/60 text-lg mystical-float">✧</span>
                <div className="w-8 h-px bg-gradient-to-l from-transparent to-[#D4AF37]/50" />
              </div>
              <p className="font-heading text-sm tracking-[0.3em] text-[#A67C00] mb-1">TOUCH TO UNLOCK</p>
              <p className="font-body text-[10px] text-white/20 italic">the secrets within await...</p>
            </div>
          )}
        </div>

        {/* Perfume Gallery — revealed when chest opens */}
        <div
          className="transition-all duration-1000 ease-out"
          style={{ 
            opacity: isOpen ? 1 : 0, 
            maxHeight: isOpen ? '1200px' : '0', 
            overflow: isOpen ? 'visible' : 'hidden',
            transform: isOpen ? 'translateY(0)' : 'translateY(40px)'
          }}
        >
          {/* Ancient revelation text */}
          <div className="text-center mb-8 mt-6">
            <div className="flex items-center justify-center gap-4 mb-3">
              <div className="w-12 h-px" style={{ background: 'linear-gradient(to right, transparent, #4a3d2e)' }} />
              <span style={{ color: '#6a5a4a' }}>◆</span>
              <div className="w-12 h-px" style={{ background: 'linear-gradient(to left, transparent, #4a3d2e)' }} />
            </div>
            <p className="font-heading text-xl md:text-2xl tracking-widest" style={{ color: '#a89878' }}>THE SACRED ELIXIRS</p>
            <p className="font-body text-sm mt-2 italic" style={{ color: '#6a5a4a' }}>"Unearthed from the depths of time..."</p>
          </div>

          {/* Perfume lineup with ANCIENT frames */}
          <div className="relative px-4 md:px-8">
            {/* Cave dust floor */}
            <div className="absolute bottom-0 left-0 right-0 h-16" style={{
              background: 'radial-gradient(ellipse at 50% 100%, rgba(60,50,35,0.3) 0%, transparent 70%)'
            }} />

            {/* Left Arrow */}
            <button
              onClick={() => scrollTreasury('left')}
              className="absolute left-0 top-1/2 -translate-y-1/2 z-20 w-10 h-10 md:w-12 md:h-12 flex items-center justify-center transition-all duration-300 hover:opacity-100 opacity-70"
              style={{
                background: 'rgba(10,8,4,0.9)',
                border: '1px solid #4a3d2e',
                color: '#a89878',
                backdropFilter: 'blur(8px)',
              }}
              data-testid="treasury-scroll-left"
            >
              <ChevronLeft size={20} />
            </button>

            {/* Right Arrow */}
            <button
              onClick={() => scrollTreasury('right')}
              className="absolute right-0 top-1/2 -translate-y-1/2 z-20 w-10 h-10 md:w-12 md:h-12 flex items-center justify-center transition-all duration-300 hover:opacity-100 opacity-70"
              style={{
                background: 'rgba(10,8,4,0.9)',
                border: '1px solid #4a3d2e',
                color: '#a89878',
                backdropFilter: 'blur(8px)',
              }}
              data-testid="treasury-scroll-right"
            >
              <ChevronRight size={20} />
            </button>
            
            {/* Scrollable container */}
            <div 
              ref={treasuryScrollRef}
              className="flex gap-6 md:gap-8 overflow-x-auto pb-12 pt-4 snap-x snap-mandatory treasury-scroll-ancient px-10 md:px-14"
              style={{ 
                scrollbarWidth: 'thin',
                scrollbarColor: '#4a3d2e #151210',
              }}
            >
              {products.map((product, index) => (
                <div
                  key={product.id}
                  className="snap-center shrink-0 cursor-pointer group"
                  style={{
                    animation: isOpen ? `riseFromChest 0.8s ease-out ${index * 0.1}s both` : 'none'
                  }}
                  onClick={() => setPopup(product)}
                  onMouseEnter={() => setSelectedIndex(index)}
                  data-testid={`boutique-product-${product.id}`}
                >
                  {/* ANCIENT Weathered Frame */}
                  <div className={`relative w-44 md:w-52 transition-all duration-500 ${selectedIndex === index ? 'scale-105 -translate-y-2' : ''}`}>
                    {/* Subtle glow on hover */}
                    <div className={`absolute -inset-6 transition-opacity duration-500 ${selectedIndex === index ? 'opacity-100' : 'opacity-0'}`} style={{
                      background: 'radial-gradient(ellipse at center, rgba(80,70,50,0.3) 0%, transparent 60%)',
                      filter: 'blur(15px)'
                    }} />
                    
                    {/* Weathered frame container */}
                    <div className="relative p-3" style={{ 
                      background: 'linear-gradient(135deg, #1a1510 0%, #2a2218 30%, #1f1a14 50%, #2a2218 70%, #1a1510 100%)',
                      boxShadow: 'inset 0 0 25px rgba(0,0,0,0.6)',
                      filter: 'sepia(0.15) saturate(0.85)',
                    }}>
                      {/* Cracked texture overlay */}
                      <div className="absolute inset-0 pointer-events-none opacity-40" style={{
                        backgroundImage: `
                          linear-gradient(45deg, transparent 40%, rgba(60,50,35,0.4) 41%, transparent 42%),
                          linear-gradient(-60deg, transparent 55%, rgba(40,35,25,0.3) 56%, transparent 57%)
                        `,
                      }} />
                      
                      {/* Worn carved border */}
                      <div className="absolute inset-1 pointer-events-none" style={{
                        border: '3px solid #3d3428',
                        boxShadow: 'inset 1px 1px 0 #4a3d2e, inset -1px -1px 0 #151210',
                      }} />
                      <div className="absolute inset-2.5 pointer-events-none" style={{
                        border: '1px solid #2a231a',
                      }} />
                      
                      {/* Ancient corner carvings */}
                      {['top-0 left-0', 'top-0 right-0 scale-x-[-1]', 'bottom-0 left-0 scale-y-[-1]', 'bottom-0 right-0 scale-[-1]'].map((pos, i) => (
                        <div key={i} className={`absolute ${pos} w-7 h-7 pointer-events-none`}>
                          <svg viewBox="0 0 28 28" className="w-full h-full opacity-50">
                            <path d="M0,0 L10,0 L10,3 L3,3 L3,10 L0,10 Z" fill="#4a3d2e" />
                            <path d="M1,1 L8,1 L8,2 L2,2 L2,8 L1,8 Z" fill="#3d3428" />
                            <circle cx="5" cy="5" r="1.5" fill="#2a231a" />
                          </svg>
                        </div>
                      ))}
                      
                      {/* Top worn ornament */}
                      <div className="absolute -top-1 left-1/2 -translate-x-1/2 pointer-events-none">
                        <svg width="36" height="10" viewBox="0 0 36 10" className="opacity-45">
                          <rect x="0" y="4" width="12" height="2" fill="#3d3428" rx="1" />
                          <polygon points="18,0 23,5 18,10 13,5" fill="#4a3d2e" />
                          <polygon points="18,2 21,5 18,8 15,5" fill="#2a231a" />
                          <rect x="24" y="4" width="12" height="2" fill="#3d3428" rx="1" />
                        </svg>
                      </div>
                      
                      {/* Bottom worn ornament */}
                      <div className="absolute -bottom-1 left-1/2 -translate-x-1/2 pointer-events-none">
                        <svg width="36" height="10" viewBox="0 0 36 10" className="opacity-45">
                          <rect x="0" y="4" width="12" height="2" fill="#3d3428" rx="1" />
                          <polygon points="18,0 23,5 18,10 13,5" fill="#4a3d2e" />
                          <polygon points="18,2 21,5 18,8 15,5" fill="#2a231a" />
                          <rect x="24" y="4" width="12" height="2" fill="#3d3428" rx="1" />
                        </svg>
                      </div>
                      
                      {/* Image container with cave effect */}
                      <div className="relative aspect-square overflow-hidden" style={{
                        background: '#080604',
                        boxShadow: 'inset 0 0 40px rgba(0,0,0,0.9)',
                      }}>
                        <img 
                          src={product.image} 
                          alt={product.name} 
                          loading="lazy" 
                          className="w-full h-full object-contain transition-all duration-700 group-hover:scale-105"
                          style={{ filter: 'sepia(0.1) brightness(0.9)' }}
                        />
                        
                        {/* Cave dust overlay */}
                        <div className="absolute inset-0 pointer-events-none" style={{
                          background: 'linear-gradient(180deg, transparent 50%, rgba(60,50,35,0.4) 100%)',
                        }} />
                        
                        {/* Aged vignette */}
                        <div className="absolute inset-0 pointer-events-none" style={{
                          boxShadow: 'inset 0 0 50px rgba(0,0,0,0.7)',
                        }} />
                        
                        {/* Ancient category seal */}
                        <div className="absolute top-2 left-2">
                          <div className="px-2 py-1" style={{
                            background: 'linear-gradient(135deg, #2a231a 0%, #3d3428 50%, #2a231a 100%)',
                            border: '1px solid #4a3d2e',
                            boxShadow: '0 2px 4px rgba(0,0,0,0.5)',
                          }}>
                            <span className="font-heading text-[7px] tracking-wider" style={{ color: '#8a7a60' }}>
                              {product.category?.toUpperCase()}
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                    
                    {/* Product info below frame */}
                    <div className="mt-4 text-center">
                      <h4 className="font-heading text-sm transition-colors leading-tight" style={{ color: selectedIndex === index ? '#a89878' : '#8a7a60' }}>{product.name}</h4>
                      <p className="font-body text-[9px] mt-0.5 italic" style={{ color: '#5a4a3a' }}>{product.subtitle}</p>
                      <div className="flex items-baseline justify-center gap-2 mt-1">
                        {product.original_price && product.original_price > product.price && (
                          <span className="font-body text-[10px] line-through" style={{ color: '#4a3d2e' }}>${product.original_price.toFixed(0)}</span>
                        )}
                        {product.price > 0 && (
                          <p className="font-heading text-lg" style={{ color: product.discount_pct ? '#D4AF37' : '#6a5a4a' }}>${product.price.toFixed(0)}</p>
                        )}
                        {product.discount_pct ? (
                          <span className="font-heading text-[9px] tracking-wider px-1.5 py-0.5" style={{ background: '#D4AF37', color: '#050505' }}>-{product.discount_pct}%</span>
                        ) : null}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
            
            {/* Scroll hint */}
            <p className="text-center font-body text-xs mt-2" style={{ color: '#4a3d2e' }}>← Scroll to view all sacred elixirs →</p>
          </div>

          {/* Ancient CTA */}
          <div className="text-center mt-8">
            <Link 
              to="/products" 
              className="inline-flex items-center gap-4 px-10 py-4 border-2 border-[#6B5210] text-[#D4AF37] font-heading text-sm tracking-[0.25em] hover:border-[#D4AF37] hover:bg-[#D4AF37]/10 transition-all duration-500" 
              style={{
                background: 'linear-gradient(180deg, rgba(107,82,16,0.1) 0%, transparent 100%)',
                boxShadow: '0 4px 20px rgba(0,0,0,0.3)'
              }}
              data-testid="explore-collection-btn"
            >
              <span className="text-lg">✧</span>
              ENTER THE FULL COLLECTION
              <span className="text-lg">✧</span>
            </Link>
          </div>
        </div>
      </div>

      {/* Product Popup (gender-aware) */}
      {popup && <ProductPopup product={popup} onClose={() => setPopup(null)} />}

      <style>{`
        @keyframes fadeSlideUp {
          from { opacity: 0; transform: translateY(40px); }
          to { opacity: 1; transform: translateY(0); }
        }
        @keyframes riseFromChest {
          0% { opacity: 0; transform: translateY(60px) scale(0.8); }
          60% { opacity: 1; transform: translateY(-10px) scale(1.02); }
          100% { opacity: 1; transform: translateY(0) scale(1); }
        }
        @keyframes scaleIn {
          from { opacity: 0; transform: scale(0.9); }
          to { opacity: 1; transform: scale(1); }
        }
        .scrollbar-hide::-webkit-scrollbar { display: none; }
        .scrollbar-hide { -ms-overflow-style: none; scrollbar-width: none; }
        .ancient-glow { text-shadow: 0 0 20px rgba(212,175,55,0.4), 0 0 40px rgba(212,175,55,0.2); }
        .mystical-float { animation: mysticalFloat 3s ease-in-out infinite; }
        @keyframes mysticalFloat {
          0%, 100% { transform: translateY(0); opacity: 0.6; }
          50% { transform: translateY(-8px); opacity: 1; }
        }
        .lock-pulse { animation: lockPulse 2s ease-in-out infinite; }
        @keyframes lockPulse {
          0%, 100% { opacity: 0.3; transform: scale(1); }
          50% { opacity: 0.6; transform: scale(1.1); }
        }
        .dust-particles {
          background-image: 
            radial-gradient(circle at 20% 20%, rgba(212,175,55,0.15) 0%, transparent 0.5%),
            radial-gradient(circle at 80% 30%, rgba(212,175,55,0.1) 0%, transparent 0.5%),
            radial-gradient(circle at 40% 70%, rgba(212,175,55,0.12) 0%, transparent 0.5%),
            radial-gradient(circle at 65% 85%, rgba(212,175,55,0.08) 0%, transparent 0.5%),
            radial-gradient(circle at 90% 60%, rgba(212,175,55,0.1) 0%, transparent 0.5%);
          background-size: 200px 200px;
          animation: dustFloat 20s linear infinite;
        }
        @keyframes dustFloat {
          0% { transform: translateY(0) translateX(0); }
          100% { transform: translateY(-100px) translateX(50px); }
        }
        .light-ray {
          animation: rayPulse 3s ease-in-out infinite;
        }
        @keyframes rayPulse {
          0%, 100% { opacity: 0.1; }
          50% { opacity: 0.3; }
        }
        .treasury-scroll::-webkit-scrollbar {
          height: 6px;
        }
        .treasury-scroll::-webkit-scrollbar-track {
          background: #151210;
          border-radius: 3px;
        }
        .treasury-scroll::-webkit-scrollbar-thumb {
          background: linear-gradient(90deg, #3d3428, #4a3d2e, #3d3428);
          border-radius: 3px;
        }
        .treasury-scroll::-webkit-scrollbar-thumb:hover {
          background: linear-gradient(90deg, #4a3d2e, #5a4d3e, #4a3d2e);
        }
        .treasury-scroll-ancient::-webkit-scrollbar {
          height: 6px;
        }
        .treasury-scroll-ancient::-webkit-scrollbar-track {
          background: #151210;
          border-radius: 3px;
        }
        .treasury-scroll-ancient::-webkit-scrollbar-thumb {
          background: linear-gradient(90deg, #3d3428, #4a3d2e, #3d3428);
          border-radius: 3px;
        }
        .treasury-scroll-ancient::-webkit-scrollbar-thumb:hover {
          background: linear-gradient(90deg, #4a3d2e, #5a4d3e, #4a3d2e);
        }
      `}</style>
    </section>
  );
};

export default EyeCatchingDivider;
